import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { HouseholdResponse } from "@/types/household"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface HouseholdCardProps {
  household: HouseholdResponse
  onEdit?: (household: HouseholdResponse) => void
  onDelete?: (household: HouseholdResponse) => void
  onView?: (household: HouseholdResponse) => void
}

const HouseholdCard = memo(
  ({ household, onEdit, onDelete, onView }: HouseholdCardProps) => {
    const titleValue = household.user_id ?? household.id

    const subtitleValue = household.parent_first_name ?? undefined

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Stack gap={1}>
          <Heading size="sm">{titleValue}</Heading>
          {subtitleValue !== undefined && (
            <Text color="fg.muted" fontSize="sm">
              {subtitleValue || "No description"}
            </Text>
          )}
        </Stack>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>
            Created: {new Date(household.created_at).toLocaleString()}
          </Text>
          <Text>
            Updated: {new Date(household.updated_at).toLocaleString()}
          </Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex
            key="user_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">User Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.user_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="parent_first_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Parent First Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.parent_first_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="parent_last_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Parent Last Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.parent_last_name, "str")}
            </Text>
          </Flex>
          <Flex key="email" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Email</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.email, "str")}
            </Text>
          </Flex>
          <Flex key="phone" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Phone</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.phone, "str")}
            </Text>
          </Flex>
          <Flex
            key="secondary_phone"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Secondary Phone</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.secondary_phone, "str")}
            </Text>
          </Flex>
          <Flex
            key="address"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Address</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.address, "str")}
            </Text>
          </Flex>
          <Flex
            key="emergency_contact_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Emergency Contact Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.emergency_contact_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="emergency_contact_phone"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Emergency Contact Phone</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.emergency_contact_phone, "str")}
            </Text>
          </Flex>
          <Flex
            key="emergency_contact_relationship"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Emergency Contact Relationship</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(
                household.emergency_contact_relationship,
                "str",
              )}
            </Text>
          </Flex>
          <Flex
            key="doctor_first_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Doctor First Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.doctor_first_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="doctor_last_name"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Doctor Last Name</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.doctor_last_name, "str")}
            </Text>
          </Flex>
          <Flex
            key="doctor_phone"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Doctor Phone</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.doctor_phone, "str")}
            </Text>
          </Flex>
          <Flex
            key="insurance_provider"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Insurance Provider</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.insurance_provider, "str")}
            </Text>
          </Flex>
          <Flex
            key="referral_source"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Referral Source</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.referral_source, "str")}
            </Text>
          </Flex>
          <Flex
            key="agreed_to_policy"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Agreed To Policy</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.agreed_to_policy, "bool")}
            </Text>
          </Flex>
          <Flex
            key="agreed_to_hold_harmless"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Agreed To Hold Harmless</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.agreed_to_hold_harmless, "bool")}
            </Text>
          </Flex>
          <Flex
            key="policy_agreement_date"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Policy Agreement Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.policy_agreement_date, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="billing_email"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Billing Email</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.billing_email, "str")}
            </Text>
          </Flex>
          <Flex
            key="auto_pay_enabled"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Auto Pay Enabled</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.auto_pay_enabled, "bool")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="is_active"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Active</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(household.is_active, "bool")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button size="sm" variant="ghost" onClick={() => onView(household)}>
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(household)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete household"
              onClick={() => onDelete(household)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

HouseholdCard.displayName = "HouseholdCard"

export default HouseholdCard
